import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import BodyMap from 'components/play/BodyMap';
import { focusLevelLabel, muscleDistributionOf, muscleLabel } from 'components/play/constants';

// Opacidad del punto de cada músculo según su nivel de enfoque (1..3). Espeja los tres tonos
// dorados que el propio BodyMap pinta sobre el SVG.
const DOT_OPACITY = { 1: 0.32, 2: 0.62, 3: 1 };

// Constante de módulo: `items` es dependencia del useMemo de distribución, y un `[]` en la firma
// se recrearía en cada render recalculando el mapa.
const DEFAULT_ITEMS = [];

/**
 * OTRIX Play — mapa muscular del home.
 *
 * Pinta sobre el cuerpo la distribución muscular de la rutina de la semana y la lista al lado.
 * Tocar un músculo abre el drawer de ejercicios (`onSelect`): antes esto reemplazaba el resto
 * de la pantalla y se perdía el contexto.
 */
function PlayMuscleMapCard({ items = DEFAULT_ITEMS, onSelect }) {
  const { t } = useTranslation(['play']);

  const distribution = useMemo(() => muscleDistributionOf(items), [items]);

  // Los más trabajados primero: la lista es un resumen, no un catálogo.
  const ranked = useMemo(
    () => Object.entries(distribution).sort((a, b) => b[1] - a[1]),
    [distribution]
  );

  return (
    <PremiumCard sx={{ height: '100%' }} innerSx={{ height: '100%' }}>
      <Stack spacing={2} sx={{ height: '100%' }}>
        <Stack spacing={0.25}>
          <Typography variant="subtitle2" fontWeight={800}>
            {t('home.map.title', { ns: 'play' })}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('home.map.hint', { ns: 'play' })}
          </Typography>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row', md: 'column', lg: 'row' }}
          spacing={2}
          alignItems={{ sm: 'center', md: 'stretch', lg: 'center' }}
          sx={{ flex: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Siempre con `distribution` (aunque venga vacía): así BodyMap no añade su propio
                hint debajo del cuerpo, que duplicaría el del encabezado. */}
            <BodyMap distribution={distribution} onSelect={onSelect} showModeToggle={false} />
          </Box>

          <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0, justifyContent: 'center' }}>
            {ranked.length === 0 ? (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('home.map.empty', { ns: 'play' })}
              </Typography>
            ) : (
              ranked.map(([muscle, level]) => (
                <Stack
                  key={muscle}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelect(muscle)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelect(muscle);
                    }
                  }}
                  sx={{
                    px: 1.25,
                    py: 0.75,
                    borderRadius: '12px',
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: 'hairline.main',
                    backgroundColor: 'surface.overlay',
                    transition: 'transform 0.22s cubic-bezier(0.32, 0.72, 0, 1), border-color 0.22s',
                    '&:hover': { transform: 'translateY(-2px)', borderColor: 'hairline.strong' },
                    '&:active': { transform: 'scale(0.98)' }
                  }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      flex: 'none',
                      backgroundColor: 'gold.main',
                      opacity: DOT_OPACITY[level] || 1
                    }}
                  />
                  <Typography variant="caption" fontWeight={700} noWrap sx={{ flex: 1, minWidth: 0 }}>
                    {muscleLabel(t, muscle)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', flex: 'none' }} noWrap>
                    {focusLevelLabel(t, level)}
                  </Typography>
                </Stack>
              ))
            )}
          </Stack>
        </Stack>
      </Stack>
    </PremiumCard>
  );
}

PlayMuscleMapCard.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  onSelect: PropTypes.func.isRequired
};

export default PlayMuscleMapCard;
